import { useEffect, useState } from "react";

// What the workspace remembers between the inbox and a case: the order of the
// inbox view the agent came from (for Next / Previous) and the last case they
// opened (highlighted when they come back). Storage can be blocked or full;
// every read and write survives that and the page still works without it.

const QUEUE_KEY = "cases.queue";
const LAST_KEY = "cases.lastOpened";
const HIDE_KEY = "cases.hideNotAsked";

function read(store, key) {
  try {
    const raw = window[store].getItem(key);
    return raw == null ? null : JSON.parse(raw);
  } catch {
    return null;
  }
}

function write(store, key, value) {
  try {
    window[store].setItem(key, JSON.stringify(value));
  } catch {
    /* private mode or full: the page works without it */
  }
}

// queue: { ids: number[], label: string, search: string, partial: boolean }
export const saveQueue = (queue) => write("sessionStorage", QUEUE_KEY, queue);

export function readQueue() {
  const queue = read("sessionStorage", QUEUE_KEY);
  return queue && Array.isArray(queue.ids) ? queue : null;
}

export const rememberOpened = (id) => write("localStorage", LAST_KEY, Number(id));
export const lastOpened = () => Number(read("localStorage", LAST_KEY)) || null;

export const readHideNotAsked = () => read("localStorage", HIDE_KEY) !== false;
export const saveHideNotAsked = (value) => write("localStorage", HIDE_KEY, !!value);

// Single-letter shortcuts on documents (A approve, N new copy) are off
// until the agent switches them on, on this computer: one stray key must
// never approve a document (QA nadia-11, georges-18; WCAG 2.1.4).
const LETTER_KEYS_KEY = "cases.letterKeys";
export const readLetterKeys = () => read("localStorage", LETTER_KEYS_KEY) === true;
export const saveLetterKeys = (value) => write("localStorage", LETTER_KEYS_KEY, !!value);

// The inbox hides the team's test accounts unless the agent asks to see them.
const HIDE_TEST_KEY = "cases.hideTest";
export const readHideTest = () => read("localStorage", HIDE_TEST_KEY) !== false;
export const saveHideTest = (value) => write("localStorage", HIDE_TEST_KEY, !!value);

// The consulate last written on a decision for this visa, to prefill the
// next one (on this computer; the API keeps no list of them).
const CONSULATE_KEY = "cases.consulates";
export const visaKey = (caseData) => [caseData?.destination, caseData?.product].filter(Boolean).join(" · ");
export function lastConsulate(caseData) {
  const all = read("localStorage", CONSULATE_KEY);
  return (all && typeof all === "object" && all[visaKey(caseData)]) || "";
}
export function rememberConsulate(caseData, consulate) {
  const value = String(consulate || "").trim();
  if (!value || !visaKey(caseData)) return;
  const all = read("localStorage", CONSULATE_KEY);
  write("localStorage", CONSULATE_KEY, { ...(all && typeof all === "object" ? all : {}), [visaKey(caseData)]: value });
}

// The time now, again every `every` ms: for what goes stale on its own (a
// code is fresh for an hour).
export function useNow(every = 30000) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), every);
    return () => clearInterval(timer);
  }, [every]);
  return now;
}

// A test account: an @example.com email or a name starting "ZZ ". Nothing
// broader: a real customer could be called Test.
export const isTestCase = (item) =>
  /@example\.com$/i.test(String(item?.customer?.email || "").trim()) || /^zz /i.test(String(item?.customer?.name || "").trim());

// True while the person is typing or a menu/dialog has the keyboard: single
// letter shortcuts must not fire then.
export function isTyping(event) {
  const target = event.target;
  if (!(target instanceof Element)) return false;
  return !!target.closest(
    'input, textarea, select, [contenteditable="true"], [role="combobox"], [role="listbox"], [role="menu"], [role="dialog"]',
  );
}

export const hasModifier = (event) => event.metaKey || event.ctrlKey || event.altKey;

// The height of the dashboard's sticky top bar, so our own sticky bars sit
// right under it with no strip of page showing. 0 when the bar does not stick.
export function useTopBarHeight() {
  const [height, setHeight] = useState(0);
  useEffect(() => {
    const bar = document.querySelector("header.MuiAppBar-root");
    if (!bar) return undefined;
    const measure = () => {
      const sticks = ["sticky", "fixed"].includes(window.getComputedStyle(bar).position);
      setHeight(sticks ? Math.round(bar.getBoundingClientRect().height) : 0);
    };
    measure();
    if (typeof ResizeObserver === "undefined") {
      window.addEventListener("resize", measure);
      return () => window.removeEventListener("resize", measure);
    }
    const observer = new ResizeObserver(measure);
    observer.observe(bar);
    return () => observer.disconnect();
  }, []);
  return height;
}

const reducedMotion = () => !!window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;

// Scrolls to an element, moves focus there and flashes it once, so the agent
// sees where a check's link took them.
export function reveal(id) {
  const element = document.getElementById(id);
  if (!element) return false;
  element.scrollIntoView({ behavior: reducedMotion() ? "auto" : "smooth", block: "center" });
  element.focus?.({ preventScroll: true });
  if (!reducedMotion() && element.animate) {
    element.animate(
      [{ backgroundColor: "rgba(0, 74, 173, 0.16)" }, { backgroundColor: "rgba(0, 74, 173, 0)" }],
      { duration: 1800, easing: "ease-out" },
    );
  }
  return true;
}

// Ids the checks link to.
export const fieldAnchor = (requestId, questionId) => `case-field-${requestId}-${questionId}`;
export const officialAnchor = (requestId, number) => `case-official-${requestId}-${number}`;
export const docAnchor = (requestId, documentId) => `case-doc-${requestId}-${documentId}`;

// Text colours with enough contrast on white for each tone (the theme's own
// error and warning mains are too light for text).
export const TONE = {
  fail: { light: "#B42318", dark: "#FDA29B" },
  warn: { light: "#8A5A00", dark: "#F5C26B" },
  pass: { light: "#18714A", dark: "#6CD3A0" },
  skip: { light: "#51627A", dark: "#B8C4D3" },
  // Said by the customer, for the agent to read (a date difference intended).
  noted: { light: "#004AAD", dark: "#5E9BEA" },
};

export const toneColor = (tone) => (theme) => TONE[tone][theme.palette.mode === "dark" ? "dark" : "light"];

// Whether the case's right column may stick under the header: only when the
// whole of it fits in the window below the header (16px to spare). A taller
// column scrolls with the page instead of inside itself.
export const railFits = (railHeight, viewportHeight, top) => railHeight > 0 && railHeight + top + 16 <= viewportHeight;
